import { useEffect, useRef } from 'react'

export type Keybindings = {
  [keys: string]: () => void
}

type Shortcut = {
  matches: (event: KeyboardEvent) => boolean
  handler: () => void
}

function createShortcut(keys: string, handler: () => void): Shortcut {
  const MOD = /Mac|iPod|iPhone|iPad/.test(navigator.platform) ? 'Meta' : 'Control'

  const [key, ...modifiers] = keys
    .split('+')
    .reverse()
    .map((mod) => mod.replace('$mod', MOD))

  return {
    matches: (event) => {
      return (
        modifiers.every((mod) => event.getModifierState(mod)) &&
        (event.key === key || event.code === key)
      )
    },
    handler: handler
  }
}

export function registerShortcuts(
  target: Window | HTMLElement,
  keyBindings: Keybindings
): () => void {
  const shortcuts = Object.entries(keyBindings).map(([keys, handler]) =>
    createShortcut(keys, handler)
  )
  function keybindingsHandler(event: Event) {
    if (!(event instanceof KeyboardEvent)) {
      return
    }

    const matchingShortcut = shortcuts.find((s) => s.matches(event))
    if (matchingShortcut) {
      event.preventDefault()
      matchingShortcut.handler()
    }
  }

  target.addEventListener('keydown', keybindingsHandler)

  return () => {
    return target.removeEventListener('keydown', keybindingsHandler)
  }
}

export function useKeyboardShortcuts(
  keyBindings: Keybindings,
  target: Window | HTMLElement = window
) {
  // Always keep the latest compiled shortcuts
  const shortcutsRef = useRef<Shortcut[]>([])

  // Re‑compile *every render* so callers don't need useMemo/useCallback.
  // The cost is negligible (string split + small array) and removes foot‑guns.
  shortcutsRef.current = Object.entries(keyBindings).map(([keys, handler]) =>
    createShortcut(keys, handler)
  )

  useEffect(() => {
    function keybindingsHandler(event: Event) {
      if (!(event instanceof KeyboardEvent)) return
      const match = shortcutsRef.current.find((s) => s.matches(event))
      if (match) {
        event.preventDefault()
        match.handler()
      }
    }

    target.addEventListener('keydown', keybindingsHandler)
    return () => target.removeEventListener('keydown', keybindingsHandler)
  }, [target])
}
